import { useContext, useCallback, useMemo, useRef } from 'react';
import { useQuery, useInfiniteQuery, useQueries } from '@tanstack/react-query';
import { AuthContext } from '../contexts/AuthContext';
import { TradingContext } from '../contexts/TradingContext';
import useAPI from './useApi';
import { getBalanceRefreshInterval } from '../utils/accountUtils';
import {
  brokerAccountsUrl,
  brokerBalancesUrl,
  brokerOrdersUrl,
  brokerPositionsUrl,
  brokerHistoricalOrdersUrl,
  brokerBarChartsUrl,
  withBrokerAccountQuery,
} from '../services/brokerApi';
import { isConnectTradeAccount, supportsMarketData, resolveMarketDataConnection, getUnsupportedAssetClassBlock } from '../utils/brokerConnection';
import { fetchPlatformTickerDetails } from '../services/platformInstruments';

// Generic fetcher that injects OAuth bearer token (reserved for future use)
const _useAuthedFetcher = () => {
  const fetchJson = async (url, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    };
    const res = await fetch(url, { ...options, headers });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const message = data?.Message || data?.error || 'Request failed';
      const error = new Error(message);
      error.status = res.status;
      error.response = data;
      throw error;
    }
    return data;
  };

  return { fetchJson };
};

// Query keys
export const queryKeys = {
  accounts: (paperTrading) => ['accounts', { paperTrading }],
  balances: (paperTrading, accountId, connectionId = null, adapterType = null) => [
    'balances',
    { paperTrading, accountId, connectionId, adapterType },
  ],
  orders: (paperTrading, accountId, connectionId = null, adapterType = null) => [
    'orders',
    { paperTrading, accountId, connectionId, adapterType },
  ],
  positions: (paperTrading, accountId, connectionId = null, adapterType = null) => [
    'positions',
    { paperTrading, accountId, connectionId, adapterType },
  ],
  bars: (ticker, interval, unit, barsback, sessionTemplate, accountId = null, adapterType = null) => [
    'bars',
    { ticker, interval, unit, barsback, sessionTemplate, accountId, adapterType }
  ],
  historicalOrders: (paperTrading, accountId, since, pageSize) => [
    'historicalOrders',
    { paperTrading, accountId, since, pageSize }
  ],
  tickerDetails: (ticker) => ['tickerDetails', { ticker }],
  chartDrawings: (ticker) => ['chartDrawings', { ticker }],
  tradeAlerts: ['tradeAlerts'],
  tradingLockouts: ['tradingLockouts'],
  enforcementAlerts: (accountId) => ['enforcementAlerts', { accountId }],
  tradeJournals: ['tradeJournals'],
  watchlists: (userId) => ['watchlists', { userId }],
  watchlistTickers: (watchlistId, userId) => ['watchlistTickers', { watchlistId, userId }],
  orderEvents: (paperTrading, accountId, symbol) => [
    'orderEvents',
    { paperTrading, accountId, symbol },
  ],
};

function accountListOrThrow(res, label) {
  if (Array.isArray(res?.Accounts)) return res.Accounts;
  const message = res?.Message || res?.error || `Failed to load ${label} accounts`;
  throw new Error(typeof message === 'string' ? message : `Failed to load ${label} accounts`);
}

export const useAccountsQuery = () => {
  // Use backend JWT fetcher
  const { request } = useAPI();
  const { user } = useContext(AuthContext);
  const brokerLinked = !!user?.hasBrokerConnection || !!user?.hasTradeStationCredentials;
  const enabled = !!user?.token && brokerLinked;
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const isDemo = user?.is_demo === true;
  
  return useQuery({
    // Include user ID in query key so cache is user-specific
    queryKey: ['accounts', 'all', user?.id, { isDemo }],
    queryFn: async () => {
      // Demo users can only access SIM accounts
      if (isDemo) {
        const simAccounts = await request(brokerAccountsUrl(true), { 'Authorization': `Bearer ${user.token}` }, 'GET');
        return {
          Accounts: accountListOrThrow(simAccounts, 'SIM').map(acc => ({ ...acc, isPaperTrading: true }))
        };
      }
      
      // Regular users fetch both SIM and live accounts.
      // A failed side used to look like an empty list. TradingContext then
      // treated the starred account as gone and deleted it on login.
      const [simAccounts, liveAccounts] = await Promise.all([
        request(brokerAccountsUrl(true), { 'Authorization': `Bearer ${user.token}` }, 'GET'),
        request(brokerAccountsUrl(false), { 'Authorization': `Bearer ${user.token}` }, 'GET')
      ]);
      
      const allAccounts = [
        ...accountListOrThrow(simAccounts, 'SIM').map(acc => ({ ...acc, isPaperTrading: true })),
        ...accountListOrThrow(liveAccounts, 'live').map(acc => ({ ...acc, isPaperTrading: false }))
      ];
      
      return { Accounts: allAccounts };
    },
    enabled,
    staleTime: 60000, // Accounts don't change often, 60 seconds
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false, // Don't refetch accounts on window focus
  });
};

/**
 * @param {boolean} paperTrading
 * @param {object} account
 * @param {{ hasActivePositions?: boolean }} [options]
 *   hasActivePositions — TS Direct only polls while the book is open; flat = no interval.
 */
export const useBalancesQuery = (paperTrading, account, { hasActivePositions = false } = {}) => {
  const { user } = useContext(AuthContext);
  const { request } = useAPI();
  const accountId = account?.AccountID;
  const connectionId = account?.ct_connection_id || account?.connection_id || null;
  const adapterType = account?.adapter_type || null;
  const connectTrade = isConnectTradeAccount(account);
  // First fetch of a page load skips the server's 60s balance cache. Later polls do not.
  const forceNextBalancesRef = useRef(true);
  
  // Only enable query if account exists, has isPaperTrading property, and it matches the paperTrading parameter
  // This prevents race conditions when switching accounts
  const brokerLinked = !!user?.hasBrokerConnection || !!user?.hasTradeStationCredentials;
  const enabled = !!accountId && !!user?.token && brokerLinked &&
    account?.hasOwnProperty('isPaperTrading') && 
    account.isPaperTrading === paperTrading;

  const accountKey = `${paperTrading}|${accountId || ''}|${connectionId || ''}|${adapterType || ''}`;
  const accountKeyRef = useRef(null);
  if (accountKeyRef.current !== accountKey) {
    accountKeyRef.current = accountKey;
    forceNextBalancesRef.current = true;
  }
  
  
  const query = useQuery({
    queryKey: queryKeys.balances(paperTrading, accountId, connectionId, adapterType),
    queryFn: async () => {
      const force = forceNextBalancesRef.current;
      forceNextBalancesRef.current = false;
      return await request(
        brokerBalancesUrl(accountId, paperTrading, connectionId, adapterType, { force }),
        { 'Authorization': `Bearer ${user.token}` },
        'GET'
      );
    },
    enabled,
    // Short enough that TS Direct position-open polling can refresh promptly
    staleTime: 1000,
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes (formerly cacheTime)
    refetchOnWindowFocus: true,
    // Dedupe remounts with React Query; account switch still invalidates explicitly.
    refetchOnMount: true,
    // TS Direct: poll only with open positions (1s futures / 5s margin).
    // Connect Trade: no REST poll — balances stream (when wired) + REST seed / position events.
    refetchInterval: (query) => {
      const balanceData = query.state.data?.Balances?.[0];
      return getBalanceRefreshInterval({
        balance: balanceData,
        account,
        hasActivePositions,
        isConnectTrade: connectTrade,
      });
    },
  });

  const refetchBalances = useCallback(({ force = false } = {}) => {
    if (force) forceNextBalancesRef.current = true;
    return query.refetch();
  }, [query]);

  return { ...query, refetchBalances };
};

/**
 * Total net worth across all LIVE (non-paper) accounts.
 *
 * Net worth = sum of each live account's `Equity` (net liquidating value: cash + market
 * value of open positions, correct regardless of asset class — futures/equities/options
 * all report it consistently). We deliberately do NOT use BuyingPower since that includes
 * margin/leverage and is not part of net worth. Paper (SIM) accounts are excluded entirely.
 *
 * Reuses the same query key as `useBalancesQuery` (['balances', { paperTrading, accountId }])
 * so the selected account's balance fetch is shared/deduped with this aggregate when it's live.
 *
 * @param {Array} accounts
 * @param {{ deferUntilReady?: boolean }} [options] - when true, wait until selected-account
 *   balances have settled so net-worth live-account GETs don't compete with the hot path.
 */
export const useNetWorthQuery = (accounts, { deferUntilReady = false } = {}) => {
  const { user } = useContext(AuthContext);
  const { request } = useAPI();

  const liveAccounts = useMemo(
    () => (Array.isArray(accounts) ? accounts.filter((acc) => acc?.isPaperTrading === false) : []),
    [accounts]
  );

  const brokerLinked = !!user?.hasBrokerConnection || !!user?.hasTradeStationCredentials;
  const enabled = brokerLinked && !!user?.token && liveAccounts.length > 0 && !deferUntilReady;

  const results = useQueries({
    queries: liveAccounts.map((account) => {
      const connectionId = account?.ct_connection_id || account?.connection_id || null;
      const adapterType = account?.adapter_type || null;
      return {
        queryKey: queryKeys.balances(false, account.AccountID, connectionId, adapterType),
        queryFn: async () => request(
          brokerBalancesUrl(account.AccountID, false, connectionId, adapterType),
          { 'Authorization': `Bearer ${user.token}` },
          'GET'
        ),
        enabled,
        staleTime: 10000,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
      };
    }),
  });

  return useMemo(() => {
    if (liveAccounts.length === 0) {
      return { netWorth: null, breakdown: [], isLoading: false, hasLiveAccounts: false, liveAccountCount: 0 };
    }

    const connections = user?.brokerConnections || [];
    const resolveBrokerName = (account) => {
      if (account?.broker_name) return account.broker_name;
      if (account?.institution_name) return account.institution_name;
      if (account?.adapter_type === 'tradestation_direct') return 'TRADESTATION';
      const connId = account?.ct_connection_id || account?.connection_id;
      if (connId) {
        const match = connections.find((c) => String(c.connection_id) === String(connId));
        if (match?.broker_name) return match.broker_name;
      }
      return null;
    };

    const isLoading = deferUntilReady || results.some((r) => r.isLoading);
    const breakdown = liveAccounts.map((account, idx) => {
      const balance = results[idx]?.data?.Balances?.[0];
      const equity = parseFloat(balance?.Equity);
      return {
        accountId: account.AccountID,
        brokerName: resolveBrokerName(account),
        netWorth: Number.isFinite(equity) ? equity : null,
        isLoading: deferUntilReady || results[idx]?.isLoading || false,
      };
    });
    const netWorth = breakdown.reduce((sum, entry) => sum + (entry.netWorth || 0), 0);

    return { netWorth, breakdown, isLoading, hasLiveAccounts: true, liveAccountCount: liveAccounts.length };
  }, [results, liveAccounts, user?.brokerConnections, deferUntilReady]);
};

export const useOrdersQuery = (paperTrading, account) => {
  const { user } = useContext(AuthContext);
  const { request } = useAPI();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const accountId = account?.AccountID;
  const connectionId = account?.ct_connection_id || account?.connection_id || null;
  const adapterType = account?.adapter_type || null;
  
  // Only enable query if account exists, has isPaperTrading property, and it matches the paperTrading parameter
  // This prevents race conditions when switching accounts
  const brokerLinked = !!user?.hasBrokerConnection || !!user?.hasTradeStationCredentials;
  // Connect Trade accounts: orders come from /historicalorders. Direct: open orders REST.
  // Scope by selected account — a sibling CT link must not disable Direct orders.
  const enabled = !!accountId && !!user?.token && brokerLinked && !isConnectTradeAccount(account) &&
    account?.hasOwnProperty('isPaperTrading') && 
    account.isPaperTrading === paperTrading;
  
  
  return useQuery({
    queryKey: queryKeys.orders(paperTrading, accountId, connectionId, adapterType),
    queryFn: async () => {
      return await request(
        brokerOrdersUrl(accountId, paperTrading, connectionId, adapterType),
        { 'Authorization': `Bearer ${user.token}` },
        'GET'
      );
    },
    enabled,
  });
};

export const usePositionsQuery = (paperTrading, account) => {
  const { user } = useContext(AuthContext);
  const { request } = useAPI();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const accountId = account?.AccountID;
  const connectionId = account?.ct_connection_id || account?.connection_id || null;
  const adapterType = account?.adapter_type || null;
  
  // Only enable query if account exists, has isPaperTrading property, and it matches the paperTrading parameter
  // This prevents race conditions when switching accounts
  const brokerLinked = !!user?.hasBrokerConnection || !!user?.hasTradeStationCredentials;
  const enabled = !!accountId && !!user?.token && brokerLinked &&
    account?.hasOwnProperty('isPaperTrading') && 
    account.isPaperTrading === paperTrading;
  
  
  return useQuery({
    queryKey: queryKeys.positions(paperTrading, accountId, connectionId, adapterType),
    queryFn: async () => {
      return await request(
        brokerPositionsUrl(accountId, paperTrading, connectionId, adapterType),
        { 'Authorization': `Bearer ${user.token}` },
        'GET'
      );
    },
    enabled,
    // Streams keep the book live; short staleTime avoids remount storms without going stale forever.
    staleTime: 10000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useHistoricalOrdersQuery = (paperTrading, account, since, pageSize = 600, endDate = null) => {
  const { user } = useContext(AuthContext);
  const { request } = useAPI();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const accountId = account?.AccountID;
  
  // Only enable query if account exists, has isPaperTrading property, and it matches the paperTrading parameter
  // This prevents race conditions when switching accounts
  const enabled = !!accountId && !!user?.token && !!since &&
    account?.hasOwnProperty('isPaperTrading') && 
    account.isPaperTrading === paperTrading;
  
  return useInfiniteQuery({
    queryKey: queryKeys.historicalOrders(paperTrading, accountId, since, pageSize),
    queryFn: async ({ pageParam }) => {
      const data = await request(
        brokerHistoricalOrdersUrl(accountId, paperTrading, since, pageSize, pageParam || null, endDate),
        { 'Authorization': `Bearer ${user.token}` },
        'GET'
      );
      return data;
    },
    getNextPageParam: (lastPage) => {
      // Return the NextToken if it exists, otherwise undefined to stop pagination
      return lastPage?.NextToken || undefined;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - historical data doesn't change often
    initialPageParam: undefined,
  });
};

export const useBarsQuery = (ticker, interval, unit, barsback, sessionTemplate, extraParams = {}) => {
  const { user } = useContext(AuthContext);
  const { selectedAccount } = useContext(TradingContext);
  const { request } = useAPI();
  const accountId = selectedAccount?.AccountID;
  const mdConn = resolveMarketDataConnection(user, selectedAccount);
  const connectionId = mdConn?.connection_id
    || selectedAccount?.ct_connection_id
    || selectedAccount?.connection_id
    || null;
  const adapterType = mdConn?.adapter_type || selectedAccount?.adapter_type || null;
  const enabled =
    !!ticker &&
    !!user?.token &&
    !!sessionTemplate &&
    !!interval &&
    !!unit &&
    supportsMarketData(user, selectedAccount) &&
    !getUnsupportedAssetClassBlock(user, selectedAccount, ticker);
  return useQuery({
    queryKey: queryKeys.bars(ticker, interval, unit, barsback, sessionTemplate, accountId, adapterType),
    queryFn: async () => {
      const qs = withBrokerAccountQuery({
        interval: String(interval),
        unit: String(unit),
        barsback: String(barsback),
        sessiontemplate: String(sessionTemplate),
        ...(extraParams?.lastdate ? { lastdate: String(extraParams.lastdate) } : {}),
        ...(extraParams?.firstdate ? { firstdate: String(extraParams.firstdate) } : {}),
      }, accountId, connectionId, adapterType);
      const url = brokerBarChartsUrl(ticker, qs);
      const res = await request(url, { 'Authorization': `Bearer ${user.token}` }, 'GET');
      
      return res;
    },
    enabled,
  });
};

// Deduplication registry for bar fetches - prevents concurrent duplicate requests
const pendingBarFetches = new Map();

/** TradeStation often returns HTTP 200 with { Error, Message } and no Bars (e.g. not entitled). */
function throwIfTradestationBarsBodyIsError(data, httpStatus) {
  if (!data || typeof data !== 'object') return;
  if (data.Message != null && data.Error != null && data.Error !== '') {
    const msg = typeof data.Message === 'string' ? data.Message : JSON.stringify(data.Message);
    const err = new Error(msg);
    err.response = data;
    err.status = httpStatus;
    throw err;
  }
  if (Array.isArray(data.Errors) && data.Errors.length > 0) {
    const first = data.Errors[0];
    const msg =
      typeof first === 'string'
        ? first
        : first?.Message || first?.message || JSON.stringify(first);
    const err = new Error(msg);
    err.response = data;
    err.status = httpStatus;
    throw err;
  }
}

// Imperative fetcher for bars (reusable across hooks/components)
// Includes deduplication - concurrent requests for the same bars will share a single fetch
export const useBarsFetcher = () => {
  const { user } = useContext(AuthContext);
  const { selectedAccount } = useContext(TradingContext);
  const accountId = selectedAccount?.AccountID;
  const mdConn = resolveMarketDataConnection(user, selectedAccount);
  const connectionId = mdConn?.connection_id
    || selectedAccount?.ct_connection_id
    || selectedAccount?.connection_id
    || null;
  const adapterType = mdConn?.adapter_type || selectedAccount?.adapter_type || null;
  const canFetchMarketData = supportsMarketData(user, selectedAccount);
  return useCallback(async ({
    ticker,
    interval,
    unit,
    barsback,
    sessionTemplate,
    lastdate,
    firstdate,
    accountId: accountIdOverride,
    connectionId: connectionIdOverride,
    adapterType: adapterTypeOverride,
  } = {}) => {
    if (!ticker || !interval || !unit || !sessionTemplate) return {};
    if (!canFetchMarketData) return { Bars: [] };
    const assetClassBlock = getUnsupportedAssetClassBlock(user, selectedAccount, ticker);
    if (assetClassBlock) {
      const err = new Error(assetClassBlock.message);
      err.code = 'BROKER_ASSET_CLASS_UNSUPPORTED';
      throw err;
    }
    const routeAccountId = accountIdOverride || accountId;
    const routeConnectionId = connectionIdOverride || connectionId;
    const routeAdapterType = adapterTypeOverride || adapterType;
    // API: barsback and firstdate are mutually exclusive - use only one
    const useTimeRange = firstdate && lastdate;
    const qs = withBrokerAccountQuery({
      interval: String(interval),
      unit: String(unit),
      sessiontemplate: String(sessionTemplate),
      ...(useTimeRange
        ? { firstdate: String(firstdate), lastdate: String(lastdate) }
        : {
            barsback: String(barsback ?? 200),
            ...(lastdate ? { lastdate: String(lastdate) } : {}),
          }),
    }, routeAccountId, routeConnectionId, routeAdapterType);
    const url = brokerBarChartsUrl(ticker, qs);

    const existingPending = pendingBarFetches.get(url);
    if (existingPending) {
      return existingPending;
    }

    const fetchPromise = (async () => {
      const headers = {
        'Content-Type': 'application/json',
        ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
      };
      const res = await fetch(url, { method: 'GET', headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message =
          data.Message ||
          data.error ||
          (typeof data.Error === 'string' ? data.Error : null) ||
          `Request failed (${res.status})`;
        const err = new Error(typeof message === 'string' ? message : JSON.stringify(message));
        err.status = res.status;
        err.response = data;
        throw err;
      }
      throwIfTradestationBarsBodyIsError(data, res.status);
      return data;
    })().finally(() => {
      pendingBarFetches.delete(url);
    });

    pendingBarFetches.set(url, fetchPromise);
    return fetchPromise;
  }, [user, selectedAccount, user?.token, canFetchMarketData, accountId, connectionId, adapterType]);
};

// Ticker details lookup with persistent caching
export const useTickerDetailsQuery = (ticker) => {
  const { request } = useAPI();
  const enabled = Boolean(ticker);

  return useQuery({
    queryKey: queryKeys.tickerDetails(ticker),
    queryFn: async () => {
      const tickerDetailsCache = (await import('../utils/tickerDetailsCache')).default;

      if (tickerDetailsCache.has(ticker)) {
        const cachedDetails = tickerDetailsCache.get(ticker);
        if (cachedDetails) {
          return cachedDetails;
        }
      }

      const response = await fetchPlatformTickerDetails(request, ticker);

      if (response && !response.Errors?.length && response.Symbols?.[0]) {
        const tickerDetails = response.Symbols[0];
        tickerDetailsCache.set(ticker, tickerDetails);
        if (tickerDetails.Symbol && tickerDetails.Symbol !== ticker) {
          tickerDetailsCache.set(tickerDetails.Symbol, tickerDetails);
        }
        return tickerDetails;
      }

      return null;
    },
    enabled,
    retry: 1,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - ticker details rarely change
    gcTime: 7 * 24 * 60 * 60 * 1000, // 7 days - keep in memory for a week
  });
};

// Chart drawings - React Query caches by ticker so multiple charts with same ticker share one request
export const useChartDrawingsQuery = (ticker, enabled = true) => {
  const { user } = useContext(AuthContext);
  const { request } = useAPI();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  return useQuery({
    queryKey: queryKeys.chartDrawings(ticker),
    queryFn: async () => {
      const url = `${BACKEND_URL}/chart_drawings/${encodeURIComponent(ticker)}`;
      const res = await request(url, { 'Authorization': `Bearer ${user.token}` }, 'GET');
      return res;
    },
    enabled: enabled && !!ticker && !!user?.token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });
};

// Infinite query for historical orders with NextToken paging.
// `since` is intentionally absent — the backend computes the lookback per broker
// (Connect Trade: full stored history; TradeStation Direct: its own since default).
// endDate is fixed on the first page and reused for every cursor page.
export const useHistoricalOrdersInfiniteQuery = (paperTrading, account, pageSize = 50, endDate = null) => {
  const { user } = useContext(AuthContext);
  const { request } = useAPI();
  const accountId = account?.AccountID;
  
  const brokerLinked = !!user?.hasBrokerConnection || !!user?.hasTradeStationCredentials;
  const enabled = !!accountId && !!user?.token && brokerLinked &&
    account?.hasOwnProperty('isPaperTrading') && 
    account.isPaperTrading === paperTrading;

  return useInfiniteQuery({
    queryKey: queryKeys.historicalOrders(paperTrading, accountId, null, pageSize),
    initialPageParam: null,
    queryFn: async ({ pageParam }) => {
      const cursor = pageParam && typeof pageParam === 'object' ? pageParam.cursor : pageParam;
      const windowEnd = (pageParam && typeof pageParam === 'object' && pageParam.endDate) || endDate;
      const result = await request(
        brokerHistoricalOrdersUrl(accountId, paperTrading, null, pageSize, cursor || null, windowEnd),
        { 'Authorization': `Bearer ${user.token}` },
        'GET'
      );
      return {
        Orders: result?.Orders || [],
        NextToken: result?.NextToken || null,
        endDate: windowEnd,
      };
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.NextToken) return undefined;
      return { cursor: lastPage.NextToken, endDate: lastPage.endDate || endDate };
    },
    enabled,
  });
};

// Deferred queries for non-critical data (trade alerts, journals, watchlists)
// These have lower priority and longer stale times to reduce initial load

// Trading lockouts query - shared cache to dedupe TradeAlertsManager instances
export const useTradingLockoutsQuery = (enabled = true) => {
  const { user } = useContext(AuthContext);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  return useQuery({
    queryKey: queryKeys.tradingLockouts,
    queryFn: async () => {
      const res = await fetch(`${BACKEND_URL}/enforcement/alerts?all_accounts=true`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      const json = await res.json();
      if (!res.ok || !json.success) return [];
      return json.alerts || [];
    },
    enabled: enabled && !!user?.token,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000,
    refetchInterval: 30000, // Refresh every 30s
    refetchOnWindowFocus: false,
  });
};

// Enforcement alerts by account - shared cache to dedupe useEnforcementStatus instances
export const useEnforcementAlertsQuery = (accountId, enabled = true) => {
  const { user } = useContext(AuthContext);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  return useQuery({
    queryKey: queryKeys.enforcementAlerts(accountId),
    queryFn: async () => {
      const res = await fetch(`${BACKEND_URL}/enforcement/alerts?account_id=${accountId}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      const json = await res.json();
      if (!res.ok || !json.success) return [];
      return json.alerts || [];
    },
    enabled: enabled && !!user?.token && !!accountId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

// Trade alerts query - deferred with 2 second delay
export const useTradeAlertsQuery = (enabled = true) => {
  const { user } = useContext(AuthContext);
  const { request } = useAPI();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  return useQuery({
    queryKey: queryKeys.tradeAlerts,
    queryFn: async () => {
      const url = `${BACKEND_URL}/trade_alerts`;
      const response = await request(url, { 'Authorization': `Bearer ${user.token}` }, 'GET');
      // API returns array directly, not { alerts: [...] }
      return Array.isArray(response) ? response : (response?.alerts || []);
    },
    enabled: enabled && !!user?.token,
    staleTime: 5 * 60 * 1000, // 5 minutes - alerts don't change often
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false,
    // Add delay before fetching to prioritize streams
    initialDataUpdatedAt: Date.now() - 2000, // 2 second delay
  });
};

// Trade journals query - deferred with 2.5 second delay
export const useTradeJournalsQuery = (enabled = true) => {
  const { user } = useContext(AuthContext);
  const { request } = useAPI();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  return useQuery({
    queryKey: queryKeys.tradeJournals,
    queryFn: async () => {
      const url = `${BACKEND_URL}/trade_journals`;
      const response = await request(url, {}, 'GET');
      return Array.isArray(response) ? response : [];
    },
    enabled: enabled && !!user?.token,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

// Watchlists query - deferred with 3 second delay
export const useWatchlistsQuery = (enabled = true) => {
  const { user } = useContext(AuthContext);
  const { request } = useAPI();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  return useQuery({
    queryKey: queryKeys.watchlists(user?.id),
    queryFn: async () => {
      const url = `${BACKEND_URL}/watchlists`;
      const response = await request(url, {}, 'GET');
      return response?.watchlists || [];
    },
    enabled: enabled && !!user?.token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false,
    // Add delay before fetching to prioritize streams
    initialDataUpdatedAt: Date.now() - 3000, // 3 second delay
  });
};

// Watchlist tickers query - deferred, only fetches when watchlist is selected
export const useWatchlistTickersQuery = (watchlistId, enabled = true) => {
  const { user } = useContext(AuthContext);
  const { request } = useAPI();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  return useQuery({
    queryKey: queryKeys.watchlistTickers(watchlistId, user?.id),
    queryFn: async () => {
      const url = `${BACKEND_URL}/watchlists/${watchlistId}/tickers`;
      const response = await request(url, {}, 'GET');
      return response?.tickers || [];
    },
    enabled: enabled && !!watchlistId && !!user?.token,
    staleTime: 3 * 60 * 1000, // 3 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false,
  });
};

/**
 * Local order_events fills for one account + symbol (chart trade markers).
 * Prefer this over broker historicalorders when plotting fills on charts.
 */
export const useOrderEventsQuery = (paperTrading, account, symbol, enabled = true) => {
  const { user } = useContext(AuthContext);
  const { request } = useAPI();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const accountId = account?.AccountID;
  const normalizedSymbol = symbol ? String(symbol).trim().toUpperCase() : '';

  const queryEnabled =
    !!enabled &&
    !!user?.token &&
    !!accountId &&
    !!normalizedSymbol &&
    account?.hasOwnProperty('isPaperTrading') &&
    account.isPaperTrading === paperTrading;

  return useQuery({
    queryKey: queryKeys.orderEvents(paperTrading, accountId, normalizedSymbol),
    queryFn: async () => {
      const url = new URL(`${BACKEND_URL}/performance/order-events`);
      url.searchParams.set('accountId', String(accountId));
      url.searchParams.set('symbol', normalizedSymbol);
      url.searchParams.set('paperTrading', String(!!paperTrading));
      const response = await request(url.toString(), { Authorization: `Bearer ${user.token}` }, 'GET');
      return Array.isArray(response?.events) ? response.events : [];
    },
    enabled: queryEnabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};


